import { useEffect, useRef, useState } from 'react';
import { ROUND_SECONDS, submitChoice } from '../services/firestore';
import { avatarForId } from '../utils/avatar';
import AppHeader from './AppHeader';
import rockImg from '../assets/rock.png';
import paperImg from '../assets/paper.png';
import scissorsImg from '../assets/scissors.png';
import './Match.css';

const CHOICES = ['rock', 'paper', 'scissors'];
const IMAGES = { rock: rockImg, paper: paperImg, scissors: scissorsImg };

// Reveal sequence timings (ms): characters slide in, clash, hold, then the
// result text fades in and stays up before handing control back to the
// normal "waiting / choose your move" view.
const REVEAL_SLIDE_MS = 600;
const REVEAL_CLASH_MS = 300;
const REVEAL_HOLD_MS = 400;
const REVEAL_RESULT_MS = 1500;

// Total lifetime of the reveal sequence. Exported so the parent Tournament
// view can keep this component mounted for a deciding round's match instead
// of switching to the round-summary screen mid-animation.
export const REVEAL_TOTAL_MS = REVEAL_SLIDE_MS + REVEAL_CLASH_MS + REVEAL_HOLD_MS + REVEAL_RESULT_MS;

// Minimum time a round's move buttons must be visible before the deadline
// can expire. moveDeadline is stamped by whichever client resolves the
// previous game (or starts the tournament) before that write even reaches
// Firestore, so slow/cold connections (App Check token fetch, first listener
// attach, etc.) can eat into or blow through the round entirely before a
// player ever sees the buttons. This floor guarantees everyone gets a real
// chance to throw instead of being auto-thrown for silently.
const MIN_VISIBLE_MS = 3000;

function Match({ match, me, roundLabel, totalRounds, playersRemaining, onForfeit }) {
  const [now, setNow] = useState(Date.now());
  const [revealStage, setRevealStage] = useState(null);
  const lastRevealedAtRef = useRef(match.resolvedAt);
  const deadlineSeenRef = useRef({ deadline: match.moveDeadline, seenAt: Date.now() });
  if (deadlineSeenRef.current.deadline !== match.moveDeadline) {
    deadlineSeenRef.current = { deadline: match.moveDeadline, seenAt: Date.now() };
  }

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(interval);
  }, []);

  // Both players' choices resolve atomically server-side, so the client
  // never observes a "both submitted, awaiting result" moment in the data —
  // it only sees the already-resolved outcome. Play the slide/clash/hold
  // sequence locally whenever a new resolution comes in, then reveal the
  // result text that was already computed.
  useEffect(() => {
    if (!match.resolvedAt || match.resolvedAt === lastRevealedAtRef.current) return;
    lastRevealedAtRef.current = match.resolvedAt;

    setRevealStage('slide');
    const toClash = setTimeout(() => setRevealStage('clash'), REVEAL_SLIDE_MS);
    const toHold = setTimeout(() => setRevealStage('hold'), REVEAL_SLIDE_MS + REVEAL_CLASH_MS);
    const toResult = setTimeout(
      () => setRevealStage('result'),
      REVEAL_SLIDE_MS + REVEAL_CLASH_MS + REVEAL_HOLD_MS
    );
    const toDone = setTimeout(
      () => setRevealStage(null),
      REVEAL_SLIDE_MS + REVEAL_CLASH_MS + REVEAL_HOLD_MS + REVEAL_RESULT_MS
    );

    return () => {
      clearTimeout(toClash);
      clearTimeout(toHold);
      clearTimeout(toResult);
      clearTimeout(toDone);
    };
  }, [match.resolvedAt]);

  const isPlayer1 = match.player1Id === me.id;
  const myChoice = isPlayer1 ? match.choice1 : match.choice2;
  const opponentChoice = isPlayer1 ? match.choice2 : match.choice1;
  const myScore = isPlayer1 ? match.score1 : match.score2;
  const opponentScore = isPlayer1 ? match.score2 : match.score1;
  const opponentName = isPlayer1 ? match.player2Name : match.player1Name;
  const opponentId = isPlayer1 ? match.player2Id : match.player1Id;
  const myLastChoice = isPlayer1 ? match.lastChoice1 : match.lastChoice2;
  const opponentLastChoice = isPlayer1 ? match.lastChoice2 : match.lastChoice1;

  const effectiveDeadline = match.moveDeadline
    ? Math.max(match.moveDeadline, deadlineSeenRef.current.seenAt + MIN_VISIBLE_MS)
    : null;

  const secondsLeft = effectiveDeadline
    ? Math.max(0, Math.ceil((effectiveDeadline - now) / 1000))
    : ROUND_SECONDS;
  const percentLeft = Math.max(0, Math.min(100, (secondsLeft / ROUND_SECONDS) * 100));

  // If time runs out before this player has chosen, auto-throw a random move.
  useEffect(() => {
    if (myChoice) return;
    if (!effectiveDeadline || now < effectiveDeadline) return;
    const randomChoice = CHOICES[Math.floor(Math.random() * CHOICES.length)];
    submitChoice(match.id, me.id, randomChoice).catch(console.error);
  }, [now, myChoice, effectiveDeadline, match.id, me.id]);

  const handlePick = (choice) => {
    if (myChoice) return;
    submitChoice(match.id, me.id, choice).catch(console.error);
  };

  const lastResultText = () => {
    if (!match.lastResult) return '';
    if (match.lastResult === 'tie') {
      // "Sudden death" only applies once both players are one win away from
      // taking the match — i.e. tied 1-1 — so the next decisive throw ends
      // it outright. Earlier ties are just a normal replay of that game.
      const isMatchPoint = myScore === 1 && opponentScore === 1;
      return isMatchPoint ? "It's a tie — sudden death, throw again!" : "It's a tie — throw again!";
    }
    const iWon = (match.lastResult === 'player1') === isPlayer1;
    return iWon ? 'You won that round!' : 'You lost that round.';
  };

  const myAvatar = avatarForId(me.id);
  const opponentAvatar = avatarForId(opponentId || 'bye');

  return (
    <div className="page match-page">
      <div className="match-stack">
      <div className="desktop-match">
        <AppHeader
          subtitle={`${roundLabel} · Live Match`}
          topRight={
            <span className="badge">
              ROUND {match.round} / {totalRounds}
            </span>
          }
          middleItems={[
            '🏆 Tournament · Bracket in progress',
            'Best of 3 · Loser eliminated',
            `${playersRemaining} players remaining`,
          ]}
          bottomLeft="⚡ Live Match"
          bottomRight={
            <span className="players-label">
              {me.name} vs {opponentName}
            </span>
          }
        />

        <div className="match-body">
          <div className="player-panel p1">
            <div className="player-inner">
              <div className="avatar-lg" style={{ background: myAvatar.background }}>
                {myAvatar.emoji}
              </div>
              <div className="pname">{me.name}</div>
              <div className="score-lg">{myScore}</div>
              <div className="win-dots">
                {[0, 1, 2].map((i) => (
                  <div key={i} className={`wdot${i < myScore ? ' won-r' : ''}`} />
                ))}
              </div>
              <div className={`player-status ${myChoice ? 'status-submitted' : 'status-waiting'}`}>
                {myChoice ? '✓ Move submitted' : '🤔 Choose your move'}
              </div>
            </div>
          </div>

          <div className="center-col">
            <div className="vs-text">VS</div>

            {revealStage ? null : (
              <>
                {match.lastResult && (
                  <p className="last-round">
                    You threw <img className="inline-icon" src={IMAGES[myLastChoice]} alt={myLastChoice} />{' '}
                    {myLastChoice}, {opponentName} threw{' '}
                    <img className="inline-icon" src={IMAGES[opponentLastChoice]} alt={opponentLastChoice} />{' '}
                    {opponentLastChoice}. {lastResultText()}
                  </p>
                )}

                {myChoice ? (
                  <p className="status">Waiting for {opponentName}...</p>
                ) : (
                  <div className="choices-wrap">
                    <div className="choose-label">Your move</div>
                    <div className="choices">
                      {CHOICES.map((choice) => (
                        <button key={choice} className="choice-btn" onClick={() => handlePick(choice)}>
                          <img className="btn-icon" src={IMAGES[choice]} alt={choice} />
                          <span className="btn-label">{choice}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="timer-wrap">
              <div className="timer-top">
                <div className="timer-lbl">Time remaining</div>
                <div className="timer-num">{String(secondsLeft).padStart(2, '0')}</div>
              </div>
              <div className="timer-bar-wrap">
                <div className="timer-bar" style={{ width: `${percentLeft}%` }} />
              </div>
            </div>
          </div>

          <div className="player-panel p2">
            <div className="player-inner">
              <div className="avatar-lg" style={{ background: opponentAvatar.background }}>
                {opponentAvatar.emoji}
              </div>
              <div className="pname">{opponentName}</div>
              <div className="score-lg">{opponentScore}</div>
              <div className="win-dots">
                {[0, 1, 2].map((i) => (
                  <div key={i} className={`wdot${i < opponentScore ? ' won-b' : ''}`} />
                ))}
              </div>
              <div className={`player-status ${opponentChoice ? 'status-submitted' : 'status-waiting'}`}>
                {opponentChoice ? '✓ Move submitted' : '⏳ Waiting...'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mobile-match">
        <AppHeader
          subtitle={`${roundLabel} · Live Match`}
          topRight={
            <span className="badge">
              ROUND {match.round} / {totalRounds}
            </span>
          }
          middleItems={[
            '🏆 Tournament · Bracket in progress',
            'Best of 3 · Loser eliminated',
            `${playersRemaining} players remaining`,
          ]}
          bottomLeft="⚡ Live Match"
          bottomRight={
            <span className="players-label">
              {me.name} vs {opponentName}
            </span>
          }
        />

        <div className="mobile-players-card">
          <div className="mobile-player p1">
            <div className="mobile-player-bar" />
            <div className="mobile-player-content">
              <div className="mobile-avatar" style={{ background: myAvatar.background }}>
                {myAvatar.emoji}
              </div>
              <div className="mobile-pname">{me.name}</div>
              <div className="mobile-score">{myScore}</div>
              <div className="win-dots">
                {[0, 1, 2].map((i) => (
                  <div key={i} className={`wdot${i < myScore ? ' won-r' : ''}`} />
                ))}
              </div>
            </div>
          </div>

          <div className="mobile-vs-col">
            <div className="mobile-vs">VS</div>
          </div>

          <div className="mobile-player p2">
            <div className="mobile-player-bar" />
            <div className="mobile-player-content">
              <div className="mobile-avatar" style={{ background: opponentAvatar.background }}>
                {opponentAvatar.emoji}
              </div>
              <div className="mobile-pname">{opponentName}</div>
              <div className="mobile-score">{opponentScore}</div>
              <div className="win-dots">
                {[0, 1, 2].map((i) => (
                  <div key={i} className={`wdot${i < opponentScore ? ' won-b' : ''}`} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {revealStage ? null : (
          <>
            {match.lastResult && (
              <p className="mobile-last-round">
                You threw <img className="inline-icon" src={IMAGES[myLastChoice]} alt={myLastChoice} />{' '}
                {myLastChoice}, {opponentName} threw{' '}
                <img className="inline-icon" src={IMAGES[opponentLastChoice]} alt={opponentLastChoice} />{' '}
                {opponentLastChoice}. {lastResultText()}
              </p>
            )}

            {myChoice ? (
              <p className="mobile-status">Waiting for {opponentName}...</p>
            ) : (
              <div className="mobile-choices-wrap">
                <div className="mobile-choose-label">Your move</div>
                <div className="mobile-choices">
                  {CHOICES.map((choice) => (
                    <button key={choice} className="mobile-choice-btn" onClick={() => handlePick(choice)}>
                      <img className="mobile-btn-icon" src={IMAGES[choice]} alt={choice} />
                      <span className="mobile-btn-label">{choice}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="mobile-timer-wrap">
          <div className="mobile-timer-top">
            <div className="mobile-timer-lbl">Time remaining</div>
            <div className="mobile-timer-num">{String(secondsLeft).padStart(2, '0')}</div>
          </div>
          <div className="timer-bar-wrap">
            <div className="timer-bar" style={{ width: `${percentLeft}%` }} />
          </div>
        </div>

        {!revealStage && (
          <button type="button" className="mobile-forfeit-link" onClick={onForfeit}>
            🏳️ Forfeit Tournament
          </button>
        )}
      </div>

      {revealStage && (
        <div className={`reveal-arena stage-${revealStage}`}>
          <div className="reveal-chars">
            <img className="reveal-char reveal-p1" src={IMAGES[myLastChoice]} alt={myLastChoice} />
            <span className="reveal-clash-fx" aria-hidden="true" />
            <img className="reveal-char reveal-p2" src={IMAGES[opponentLastChoice]} alt={opponentLastChoice} />
          </div>
          {revealStage === 'result' && <p className="reveal-result-text">{lastResultText()}</p>}
        </div>
      )}

      {!revealStage && (
        <button className="btn-ghost match-forfeit" onClick={onForfeit}>
          🏳️ Forfeit Tournament
        </button>
      )}
      </div>
    </div>
  );
}

export default Match;
